import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ButtonCP } from 'submodules/nerit-framework-ui/common/components/button/ButtonCP'
import { InputCP } from 'submodules/nerit-framework-ui/common/components/form-fields/input/InputCP'
import { IconICP } from 'submodules/nerit-framework-ui/common/components/icon/inner/IconICP'
import { LoginFormModel } from 'app/components/form-login/form-model/LoginFormModel'
import { useRequest } from 'submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest'
import { RequestUtils } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils'
import { AuthActions } from 'app/utils/AuthActions'
import { NeritFrameworkRoutingHelper } from 'submodules/nerit-framework-ui/common/router/utils/NeritFrameworkRoutingHelper'
import { IFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/types/IFormStateManager'
import { FlexCP } from 'submodules/nerit-framework-ui/common/components/flex/FlexCP'
import { AuthResponseDTO } from 'submodules/nerit-aquatec-sdk/services/auth/dtos/responses/AuthResponseDTO'
import { UserLoginRequestDTO } from 'submodules/nerit-aquatec-sdk/services/auth/dtos/requests/UserLoginRequestDTO'
import { AuthRequests } from 'submodules/nerit-aquatec-sdk/services/auth/AuthRequests'
import { PrivateRouter } from 'app/routers/private/PrivateRouter'
import { FormLoginAlertsICP } from 'app/components/form-login/inner/FormLoginAlertsICP'
import {
    LOGIN_INVALID_CREDENTIALS_MESSAGE,
    LOGIN_CAPS_LOCK_LISTEN_EVENTS,
    LOGIN_PASSWORD_FIELD,
    LOGIN_USERNAME_FIELD,
    LoginPasswordVisibilityTP,
    LoginSubmitAlertTP,
    canSubmitLoginWithCapsLock,
    canSubmitLoginWithPasswordVisibility,
    capsLockFromModifierEvent,
    nextSubmitAlertAfterFieldChange,
    passwordEyeIconName,
    passwordInputType,
    planLoginRequestOutcome,
    planLoginSubmit,
    shouldSubmitLoginOnEnter,
    togglePasswordVisibility,
} from 'app/components/form-login/inner/loginFormFeedback'

interface ICPProps {
    formStateManager: IFormStateManager<LoginFormModel>
    onResetPassword: () => void
}

/**
 */
export function FormLoginDataICP(props: ICPProps): JSX.Element {

    const loginRequest = useRequest<AuthResponseDTO>()
    const [submitAlert, setSubmitAlert] = useState<LoginSubmitAlertTP>()
    const [isCapsLockOn, setIsCapsLockOn] = useState(false)
    const [passwordVisibility, setPasswordVisibility] = useState<LoginPasswordVisibilityTP>('hidden')
    useEffect(onLoginRequestChange, [loginRequest.isAwaiting])
    useEffect(listenCapsLock, [])
    useEffect(hideEmptyFieldsAlertWhenFilled, [
        submitAlert,
        props.formStateManager.getFieldValue('username'),
        props.formStateManager.getFieldValue('password'),
    ])

    /**
     */
    async function doLogin(): Promise<void> {

        const plan = planLoginSubmit({
            isAwaiting: loginRequest.isAwaiting,
            username: props.formStateManager.getFieldValue('username'),
            password: props.formStateManager.getFieldValue('password'),
        })

        if (!plan.shouldRunLoginRequest) {
            if (plan.submitAlert === 'emptyFields') {
                props.formStateManager.setConsiderAllErrors(true)
                await props.formStateManager.validate()
                setSubmitAlert('emptyFields')
            }
            return
        }

        if (!canSubmitLoginWithPasswordVisibility(passwordVisibility) || !canSubmitLoginWithCapsLock(isCapsLockOn))
            return

        props.formStateManager.setConsiderAllErrors(true)
        if (!await props.formStateManager.validate()) {
            setSubmitAlert('emptyFields')
            return
        }

        setSubmitAlert(undefined)

        const formValues = props.formStateManager.getFormValues()!
        const dto: UserLoginRequestDTO = {
            username: formValues.username,
            password: formValues.password,
        }
        loginRequest.runRequest(AuthRequests.login(dto))
    }

    /**
     */
    function listenCapsLock(): () => void {

        function onModifierEvent(event: KeyboardEvent | MouseEvent): void {
            const isCapsLockActive = capsLockFromModifierEvent(event)
            if (isCapsLockActive === undefined)
                return

            setIsCapsLockOn(isCapsLockActive)
        }

        LOGIN_CAPS_LOCK_LISTEN_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, onModifierEvent)
        })

        return () => {
            LOGIN_CAPS_LOCK_LISTEN_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, onModifierEvent)
            })
        }
    }

    /**
     */
    function onPasswordToggleClick(event: React.MouseEvent): void {
        event.preventDefault()
        event.stopPropagation()
        setPasswordVisibility(togglePasswordVisibility(passwordVisibility))
    }

    /**
     */
    function onPasswordToggleKeyPress(event: React.KeyboardEvent): void {
        event.stopPropagation()
        if (event.key === 'Enter' && !shouldSubmitLoginOnEnter(event.target))
            event.preventDefault()
    }

    /**
     */
    function hideEmptyFieldsAlertWhenFilled(): void {
        setSubmitAlert(nextSubmitAlertAfterFieldChange(
            submitAlert,
            props.formStateManager.getFieldValue('username'),
            props.formStateManager.getFieldValue('password'),
        ))
    }

    /**
     */
    function onLoginRequestChange(): void {

        if (!RequestUtils.isRequestConcluded(loginRequest))
            return

        const outcome = planLoginRequestOutcome({
            isSuccess: RequestUtils.isRequestSuccess(loginRequest),
            isError: RequestUtils.isRequestError(loginRequest),
            status: loginRequest.responseStatus,
        })

        if (outcome.shouldNavigateHome) {
            setSubmitAlert(undefined)
            const auth = loginRequest.responseData!
            AuthActions.setLoggedUser(auth.user, auth.token)
            NeritFrameworkRoutingHelper.historyPush(PrivateRouter.HOME)
            return
        }

        if (outcome.submitAlert) {
            setSubmitAlert(outcome.submitAlert)
            return
        }

        if (outcome.shouldShowCredentialsToast)
            RequestUtils.isValidRequestReturn(loginRequest, LOGIN_INVALID_CREDENTIALS_MESSAGE)
    }

    return (
        <>
            <FormLoginAlertsICP
                capsLock={isCapsLockOn}
                submitAlert={submitAlert}
            />

            <InputCP
                label={'Usuário'}
                fieldName={LOGIN_USERNAME_FIELD}
                formStateManager={props.formStateManager}
                required={true}
                icon={<IconICP iconName={'user'}/>}
                onFormSubmit={doLogin}
            />

            <InputCP
                label={'Senha'}
                fieldName={LOGIN_PASSWORD_FIELD}
                type={passwordInputType(passwordVisibility)}
                formStateManager={props.formStateManager}
                required={true}
                icon={(
                    <PasswordToggleSCP
                        type={'button'}
                        data-login-password-toggle={'true'}
                        onClick={onPasswordToggleClick}
                        onKeyPress={onPasswordToggleKeyPress}
                    >
                        <IconICP iconName={passwordEyeIconName(passwordVisibility)}/>
                    </PasswordToggleSCP>
                )}
                onFormSubmit={doLogin}
            />

            <FlexCP
                justifyContent={'space-between'}
                alignItems={'center'}
                margin={{ top: 20 }}
            >
                <RecoverPasswordButtonSCP
                    type={'button'}
                    onClick={props.onResetPassword}
                >
                    Recuperar senha
                </RecoverPasswordButtonSCP>

                <ButtonCP
                    type={'primary'}
                    onClick={doLogin}
                    loading={loginRequest.isAwaiting}
                >
                    Entrar
                </ButtonCP>
            </FlexCP>
        </>
    )
}

const PasswordToggleSCP = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`

const RecoverPasswordButtonSCP = styled.button`
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	text-decoration: underline;
`